// Typed admin API client for the llm-saas surface (providers, consumers,
// usage). Endpoints are all under /api/admin/* and rely on the session
// cookie attached by apiFetch's `credentials: include`.
//
// There is intentionally no models catalog. Each consumer key picks the
// provider; the actual model id is taken from each request's `model`
// field at chat time, so a global models table would be redundant.

import { apiFetch } from "./client"

// ─── Providers ────────────────────────────────────────────────────────────

export type ProviderKind = "claude_cli" | "codex_cli" | "opencode_cli" | "openai_http"

export interface Provider {
  id: number
  name: string
  kind: ProviderKind
  config_json: string
  has_secret: boolean
  enabled: boolean
  concurrency: number
  last_ping_status?: string
}

export interface ProviderInput {
  name: string
  kind: ProviderKind
  config_json: string
  /** Plaintext API key. Only sent on create/edit — server stores the encrypted form. */
  api_key?: string
  enabled?: boolean
  concurrency?: number
}

// ─── Consumers ────────────────────────────────────────────────────────────

export interface Consumer {
  id: number
  name: string
  key_prefix: string
  status: "active" | "revoked"
  note?: string
  provider_id: number
  provider_name?: string
  created_at: string
  last_used_at?: string
}

export interface ConsumerCreateInput {
  name: string
  note?: string
  provider_id: number
}

/**
 * PATCH /api/admin/consumers/{id} body. All fields are optional — only the
 * keys present in the request are mutated. The raw API key is NOT rotated;
 * for that, call rotateConsumer.
 */
export interface ConsumerUpdateInput {
  name?: string
  note?: string
  provider_id?: number
}

/** Returned by create + rotate. raw_key is shown ONCE — copy immediately. */
export interface ConsumerCreated {
  id: number
  name: string
  key_prefix: string
  raw_key: string
  provider_id: number
}

// ─── Usage ────────────────────────────────────────────────────────────────

export interface UsageRow {
  id: number
  request_id: string
  consumer_id: number | null
  consumer_name: string
  model_name: string
  provider_name?: string | null
  status_code: number
  prompt_tokens: number | null
  completion_tokens: number | null
  total_tokens: number | null
  tokens_estimated: boolean
  streamed: boolean
  latency_ms: number
  error_code?: string | null
  error_message?: string | null
  started_at: string
  ended_at: string
}

export interface UsageList {
  page: number
  page_size: number
  rows: UsageRow[]
}

export interface UsageSummaryTotals {
  requests: number
  prompt_tokens: number
  completion_tokens: number
  error_count: number
  p50_latency_ms: number
  p95_latency_ms: number
}

export interface UsageSummaryBucket {
  bucket: string
  requests: number
  prompt_tokens: number
  completion_tokens: number
  error_count: number
  avg_latency_ms: number
}

/**
 * Long-form (date × consumer) breakdown returned alongside the daily bucket
 * series. Shape: one row per (bucket, consumer_name) pair. The Usage page
 * pivots this client-side to feed a stacked bar chart (one Bar per
 * consumer).
 */
export interface UsageDailyByConsumerRow {
  bucket: string
  consumer_name: string
  requests: number
  total_tokens: number
}

export interface UsageSummary {
  totals: UsageSummaryTotals
  daily: UsageSummaryBucket[]
  daily_by_consumer: UsageDailyByConsumerRow[]
  by_consumer: UsageSummaryBucket[]
  by_model: UsageSummaryBucket[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries: [string, string][] = []
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === null) continue
    entries.push([k, String(v)])
  }
  if (entries.length === 0) return ""
  return "?" + new URLSearchParams(entries).toString()
}

// ─── API ──────────────────────────────────────────────────────────────────

export const llmApi = {
  // providers
  listProviders: () => apiFetch<Provider[]>("/api/admin/providers"),
  createProvider: (b: ProviderInput) =>
    apiFetch<Provider>("/api/admin/providers", { method: "POST", body: b }),
  updateProvider: (id: number, b: Partial<ProviderInput>) =>
    apiFetch<Provider>(`/api/admin/providers/${id}`, { method: "PATCH", body: b }),
  deleteProvider: (id: number) =>
    apiFetch<void>(`/api/admin/providers/${id}`, { method: "DELETE" }),
  pingProvider: (id: number) =>
    apiFetch<{ status: string }>(`/api/admin/providers/${id}/test`, { method: "POST" }),
  /**
   * Lists the upstream's known model ids. openai_http hits a real
   * GET /v1/models; CLI providers return a curated slice. Used to
   * populate the consumer issuance form's remote_model autocomplete.
   * Free-form input is still accepted on submit — this is UX, not a
   * whitelist.
   */
  discoverModels: (providerID: number) =>
    apiFetch<{ models: string[] }>(`/api/admin/providers/${providerID}/discover-models`),

  // consumers
  listConsumers: () => apiFetch<Consumer[]>("/api/admin/consumers"),
  createConsumer: (b: ConsumerCreateInput) =>
    apiFetch<ConsumerCreated>("/api/admin/consumers", { method: "POST", body: b }),
  updateConsumer: (id: number, b: ConsumerUpdateInput) =>
    apiFetch<Consumer>(`/api/admin/consumers/${id}`, { method: "PATCH", body: b }),
  rotateConsumer: (id: number) =>
    apiFetch<ConsumerCreated>(`/api/admin/consumers/${id}/rotate`, { method: "POST" }),
  /**
   * Two-stage delete: an active row is soft-deleted (status flips to
   * "revoked"); a second call on the same id hard-deletes the row.
   * The response body indicates which mode happened so the UI can show
   * the right toast.
   */
  deleteConsumer: (id: number) =>
    apiFetch<{ deleted: "revoked" | "hard" }>(`/api/admin/consumers/${id}`, { method: "DELETE" }),

  // usage
  listUsage: (params: {
    from?: string
    to?: string
    consumer?: string
    model?: string
    status?: string
    page?: number
  }) =>
    apiFetch<UsageList>(`/api/admin/usage${buildQuery(params)}`),
  summary: (params: { from?: string; to?: string }) =>
    apiFetch<UsageSummary>(`/api/admin/usage/summary${buildQuery(params)}`),
}
